import { useSearchParams } from 'react-router-dom';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { createForm, useCreateFormStore } from '@components/form';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateAuthentication } from 'api/authentication/hooks';
import { useResendSignupConfirmation, useSignupCompanyConfirm } from 'api/users/hooks';

import { linkProps, Title } from './common';

import { routePaths } from '../navigation';

export type ConfirmCodeFormData = {
  confirmationCode: string;
};

const Form = createForm<ConfirmCodeFormData>();

export const ConfirmCodePage = () => {
  const { t, styledTranslate } = useScopedTranslate(locale);
  const [searchParams] = useSearchParams();
  const formStore = useCreateFormStore();
  const confirmationCode = formStore.useFieldValue<string>('confirmationCode');
  const userEmail = searchParams.get('email')?.replace(' ', '+') || '';
  const { invalidateCurrentUser } = useInvalidateAuthentication();

  const signupConfirmMutation = useSignupCompanyConfirm({ onSuccess: invalidateCurrentUser });

  const resendMutation = useResendSignupConfirmation();

  const handleContinue = (data: ConfirmCodeFormData) => {
    signupConfirmMutation.mutate({ businessEmail: userEmail, ...data });
  };

  const handleResend = async () => {
    showSuccess(t('resendSuccess'));
    resendMutation.mutate({ businessEmail: userEmail });
  };

  return (
    <Container>
      <Stack space={6} align="center" justify="center">
        <Stack align="center">
          <Title>{t('title')}</Title>
          <Text text={t('subtitle')} font="bold18" color="textHelper" />
        </Stack>
        <Text
          text={t('inboxDetails', { email: userEmail })}
          font="regular14"
          color="textHelper"
          center
        />
        <Form onSubmit={handleContinue} store={formStore}>
          <Stack space={6}>
            <Stack space={1}>
              <Form.TextField
                id="confirmationCode"
                label={t('confirmationCode')}
                hideRequiredIndicator
                autoFocus
              />
              <Text
                color="textSecondary"
                font="regular14"
                text={styledTranslate('codeDetails', {
                  resend: resendMutation.isIdle ? (
                    <ActionText
                      {...linkProps}
                      type="button"
                      underline={false}
                      onClick={handleResend}
                    >
                      {t('resend')}
                    </ActionText>
                  ) : (
                    <Text
                      text={t('resent')}
                      font="regular14"
                      color="textSecondary"
                      style={{ display: 'inline-block' }}
                    />
                  ),
                })}
              />
            </Stack>

            <Form.Submit
              variant="primary-blue"
              text={t('continueWithCode')}
              isDisabled={
                signupConfirmMutation.isLoading ||
                !confirmationCode ||
                confirmationCode.length !== 6
              }
            />
            <ActionText.Link {...linkProps} underline={false} to={routePaths.signin}>
              {t('backToSignin')}
            </ActionText.Link>
          </Stack>
        </Form>
      </Stack>
    </Container>
  );
};

const Container = styled.div({
  form: {
    width: '100%',
  },
  a: {
    textAlign: 'center',
  },
});

const locale = {
  en: {
    title: 'Check your email',
    subtitle: "We've sent you a temporary login code",
    continueWithCode: 'Continue with signup code',
    codeDetails: 'We sent a code to your inbox • {resend}',
    confirmationCode: 'Signup code',
    resend: 'Resend',
    resent: 'Resent',
    resendSuccess: 'Confirmation code resent',
    backToSignin: 'Back to signin',
    inboxDetails: 'Please check your inbox at\n{email}',
  },
};
